/**
 * HelpTip.tsx — 字段名旁的「?」说明：点击展开气泡讲清该字段的含义与口径。
 * 气泡用 fixed 定位（抽屉正文是 overflow:auto 容器，absolute 会被裁），点外部 / Esc / 滚动 / 缩放即关。
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';

const BUBBLE_WIDTH = 260;

export function HelpTip({ label, children }: { label: string; children: ReactNode }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || bubbleRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [pos]);

  function toggle() {
    if (pos) {
      setPos(null);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(8, Math.min(rect.left - 8, window.innerWidth - BUBBLE_WIDTH - 8));
    setPos({ left, top: rect.bottom + 6 });
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`${label}说明`}
        aria-expanded={pos !== null}
        onClick={toggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          height: 14,
          marginLeft: 5,
          padding: 0,
          verticalAlign: 'middle',
          border: '1px solid var(--border-strong)',
          borderRadius: '50%',
          background: pos ? 'var(--accent-soft)' : 'transparent',
          color: pos ? 'var(--accent-fg)' : 'var(--fg-muted)',
          fontSize: 9.5,
          fontWeight: 600,
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        ?
      </button>
      {pos && (
        <div
          ref={bubbleRef}
          role="tooltip"
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            width: BUBBLE_WIDTH,
            zIndex: 300,
            padding: '8px 10px',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            background: 'var(--bg-elev)',
            boxShadow: 'var(--shadow-md)',
            color: 'var(--fg)',
            fontSize: 12,
            lineHeight: 1.55,
            fontWeight: 400,
          }}
        >
          {children}
        </div>
      )}
    </>
  );
}
